import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { ChevronLeft, ChevronRight, Truck, Plus, MapPin } from "lucide-react";
import { Link } from "react-router-dom";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const buildMonthGrid = (anchor) => {
  const first = startOfMonth(anchor);
  const last = endOfMonth(anchor);
  // Monday-first: getDay returns 0(Sun)-6(Sat)
  const firstWeekday = (first.getDay() + 6) % 7; // 0=Mon
  const days = [];
  // Leading blanks
  for (let i = 0; i < firstWeekday; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(new Date(first.getFullYear(), first.getMonth(), d));
  }
  // Trailing to fill week
  while (days.length % 7 !== 0) days.push(null);
  return days;
};

const overlapsDay = (booking, dayDate) => {
  const dStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 0, 0).getTime();
  const dEnd = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 23, 59, 59).getTime();
  const bStart = new Date(booking.rental_start).getTime();
  const bEnd = new Date(booking.rental_end).getTime();
  return bStart <= dEnd && bEnd >= dStart;
};

const occupancyForDay = (bookings, dayDate) => {
  // returns { covered: 0..1, count }
  const dStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 0, 0).getTime();
  const dEnd = dStart + 24 * 3600 * 1000;
  let covered = 0;
  let count = 0;
  for (const b of bookings) {
    const bStart = Math.max(dStart, new Date(b.rental_start).getTime());
    const bEnd = Math.min(dEnd, new Date(b.rental_end).getTime());
    if (bEnd > bStart) {
      covered += bEnd - bStart;
      count += 1;
    }
  }
  return { covered: covered / (24 * 3600 * 1000), count };
};

const VehicleMonth = ({ vehicle, bookings, anchor, onDayClick }) => {
  const grid = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const isMaster = vehicle.tariff_type === "master_package";
  return (
    <div className="bg-white border-2 border-[var(--rs-text)] p-5" data-testid={`calendar-${vehicle.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-[Chivo] text-xl font-black">{vehicle.name}</h3>
          <div className="text-xs text-[var(--rs-text-muted)]">
            {vehicle.plate}
            {(vehicle.station_id || (vehicle.flexible_stations || []).length > 0) && (
              <span className="ml-2 text-[var(--rs-primary)] font-bold uppercase tracking-[0.15em]">
                · {[vehicle.station_id, ...(vehicle.flexible_stations || []).filter((s) => s !== vehicle.station_id)]
                    .filter(Boolean)
                    .map((sid) => ({ st_gallen: "St. Gallen", st_gallen_wiesental: "St. Gallen Wiesental", bronschhofen: "Bronschhofen", bern: "Bern", derendingen: "Derendingen" })[sid] || sid)
                    .join(" / ")}
              </span>
            )}
            {isMaster && <span className="ml-2 text-red-700 font-bold">· Mi geschlossen</span>}
          </div>
        </div>
        <Truck size={22} className="text-[var(--rs-primary)]" />
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-[var(--rs-text-muted)] mb-2">
        {["Mo","Di","Mi","Do","Fr","Sa","So"].map((d) => (
          <div key={d} className="text-center font-bold">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((d, idx) => {
          if (!d) return <div key={idx} className="aspect-square" />;
          const isWed = d.getDay() === 3;
          const masterBlocked = isMaster && isWed;
          const occ = occupancyForDay(bookings, d);
          let cls = "bg-[var(--rs-surface)] hover:bg-[var(--rs-surface-2)] text-[var(--rs-text)]";
          if (masterBlocked) {
            cls = "bg-gray-200 text-gray-400 cursor-not-allowed line-through";
          } else if (occ.covered >= 0.99) {
            cls = "bg-red-600 hover:bg-red-700 text-white border border-red-800 shadow-md font-black";
          } else if (occ.covered > 0) {
            cls = "bg-red-400 hover:bg-red-500 text-white border border-red-600";
          } else {
            cls = "bg-emerald-50 text-[var(--rs-text)] hover:bg-emerald-100 border border-emerald-200";
          }
          return (
            <button
              key={idx}
              type="button"
              disabled={masterBlocked}
              onClick={() => !masterBlocked && onDayClick(d, vehicle, bookings.filter((b) => overlapsDay(b, d)))}
              className={`aspect-square text-xs font-bold flex items-center justify-center transition-colors ${cls}`}
              data-testid={`day-${vehicle.id}-${dayKey(d)}`}
              title={masterBlocked ? "Mittwoch geschlossen" : `${dayKey(d)} · ${occ.count} Buchung(en)`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const HourlyDrawer = ({ open, onClose, day, vehicle, bookings }) => {
  if (!open || !day || !vehicle) return null;

  // map bookings to hour slots 0..23
  const slots = HOURS.map((h) => {
    const slotStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, 0).getTime();
    const slotEnd = slotStart + 3600 * 1000;
    const hits = bookings.filter((b) => {
      const bs = new Date(b.rental_start).getTime();
      const be = new Date(b.rental_end).getTime();
      return bs < slotEnd && be > slotStart;
    });
    return { h, booked: hits.length > 0, bookings: hits };
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="hour-drawer">
      <div className="bg-white border-4 border-[var(--rs-primary)] w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--rs-border)] bg-[var(--rs-primary)] text-white">
          <div>
            <div className="rs-overline !text-white/60">{vehicle.name}</div>
            <h3 className="font-[Chivo] text-2xl font-black mt-1">
              {day.toLocaleDateString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white text-xs uppercase tracking-[0.2em]" data-testid="hour-drawer-close">×</button>
        </div>
        <div className="p-5 grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-1">
          {slots.map((s) => (
            <div
              key={s.h}
              className={`text-center text-[10px] py-3 border ${
                s.booked
                  ? "bg-red-600 text-white border-red-800 font-bold"
                  : "bg-emerald-50 text-[var(--rs-text)] border-emerald-200"
              }`}
              title={s.bookings.map((b) => b.contract_number).join(", ") || "frei"}
              data-testid={`hour-${s.h}`}
            >
              {String(s.h).padStart(2, "0")}
            </div>
          ))}
        </div>
        <div className="px-5 pb-5">
          <div className="rs-overline mb-2">Buchungen an diesem Tag</div>
          {bookings.length === 0 ? (
            <div className="text-sm text-[var(--rs-text-muted)]">Keine Buchungen</div>
          ) : (
            <ul className="text-sm space-y-2">
              {bookings.map((b) => (
                <li key={b.id} className="flex items-center justify-between border-b border-dashed border-[var(--rs-border)] pb-1">
                  <span className="font-bold">{b.contract_number}</span>
                  <span className="text-xs text-[var(--rs-text-muted)]">
                    {(b.rental_start || "").replace("T", " ")} → {(b.rental_end || "").replace("T", " ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const CalendarPage = () => {
  const { t } = useLanguage();
  const [data, setData] = useState({ vehicles: [], stations: [], bookings: [] });
  const [anchor, setAnchor] = useState(() => new Date());
  const [drawer, setDrawer] = useState({ open: false, day: null, vehicle: null, bookings: [] });
  const [stationFilter, setStationFilter] = useState("all"); // 'all' | station id

  useEffect(() => {
    api.get("/calendar?days=120").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const monthLabel = anchor.toLocaleDateString("de-CH", { month: "long", year: "numeric" });
  const prev = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const next = () => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));

  const bookingsByVehicle = useMemo(() => {
    const map = {};
    for (const b of data.bookings || []) {
      (map[b.vehicle_id] = map[b.vehicle_id] || []).push(b);
    }
    return map;
  }, [data]);

  const filteredVehicles = useMemo(() => {
    if (stationFilter === "all") return data.vehicles || [];
    return (data.vehicles || []).filter((v) => {
      // Include vehicle if EITHER its primary station OR any of its flex stations match
      if (v.station_id === stationFilter) return true;
      if ((v.flexible_stations || []).includes(stationFilter)) return true;
      return false;
    });
  }, [data.vehicles, stationFilter]);

  return (
    <div className="rs-app min-h-screen bg-white">
      <Header />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="rs-overline">NIVARA · KALENDER</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-2">
          <div>
            <h1 className="font-[Chivo] text-4xl sm:text-5xl font-black tracking-tight leading-[1]">
              {t("calendar_title")}
            </h1>
            <p className="text-[var(--rs-text-muted)] mt-2">{t("calendar_sub")}</p>
          </div>
          <Link to="/" className="rs-btn-primary self-start" data-testid="calendar-book-btn">
            <Plus size={16} className="mr-2" />
            {t("book_now")}
          </Link>
        </div>

        {/* Station filter */}
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="station-filter">
          <span className="rs-overline mr-2 flex items-center gap-1"><MapPin size={11} /> Standort</span>
          <button
            type="button"
            onClick={() => setStationFilter("all")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] border-2 transition-colors ${
              stationFilter === "all"
                ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                : "border-[var(--rs-border)] hover:border-[var(--rs-primary)] text-[var(--rs-text)]"
            }`}
            data-testid="station-filter-all"
          >
            Alle ({(data.vehicles || []).length})
          </button>
          {(data.stations || []).map((s) => {
            const count = (data.vehicles || []).filter((v) =>
              v.station_id === s.id || (v.flexible_stations || []).includes(s.id)
            ).length;
            const sel = stationFilter === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setStationFilter(s.id)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.15em] border-2 transition-colors ${
                  sel
                    ? "border-[var(--rs-primary)] bg-[var(--rs-primary)] text-white"
                    : "border-[var(--rs-border)] hover:border-[var(--rs-primary)] text-[var(--rs-text)]"
                }`}
                data-testid={`station-filter-${s.id}`}
              >
                {s.name} ({count})
              </button>
            );
          })}
        </div>

        {/* Legend + Month nav */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] font-bold flex-wrap">
            <span className="flex items-center gap-2"><span className="w-4 h-4 bg-emerald-50 border border-emerald-200 inline-block" /> {t("calendar_legend_free")}</span>
            <span className="flex items-center gap-2"><span className="w-4 h-4 bg-red-400 border border-red-600 inline-block" /> {t("calendar_legend_partial")}</span>
            <span className="flex items-center gap-2"><span className="w-4 h-4 bg-red-600 border border-red-800 inline-block" /> {t("calendar_legend_booked")}</span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={prev} className="rs-btn-ghost" data-testid="cal-prev">
              <ChevronLeft size={16} />
            </button>
            <div className="font-[Chivo] text-xl font-black px-4 capitalize" data-testid="cal-month">{monthLabel}</div>
            <button type="button" onClick={next} className="rs-btn-ghost" data-testid="cal-next">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
          {filteredVehicles.length === 0 ? (
            <div className="lg:col-span-2 text-center py-16 text-[var(--rs-text-muted)] border-2 border-dashed border-[var(--rs-border)]">
              Keine Fahrzeuge an diesem Standort.
            </div>
          ) : (
            filteredVehicles.map((v) => (
              <VehicleMonth
                key={v.id}
                vehicle={v}
                bookings={bookingsByVehicle[v.id] || []}
                anchor={anchor}
                onDayClick={(d, veh, bs) => setDrawer({ open: true, day: d, vehicle: veh, bookings: bs })}
              />
            ))
          )}
        </div>
      </div>

      <HourlyDrawer
        open={drawer.open}
        day={drawer.day}
        vehicle={drawer.vehicle}
        bookings={drawer.bookings}
        onClose={() => setDrawer({ open: false, day: null, vehicle: null, bookings: [] })}
      />
    </div>
  );
};

export default CalendarPage;
